<template>
  <label
    :class="{ 'btn btn-primary': true, disabled: disabled }"
    @click="onUploadClick"
  >
    <span class="glyphicons icon-left upload" /><span>{{ title || $t('upload_button.select_file') }}</span>
    <input
      :id="inputId"
      ref="uploadField"
      :disabled="disabled"
      :name="inputName"
      style="display: none"
      type="file"
      @change="uploadFile"
    >
  </label>
</template>

<script setup lang="ts" generic="TUploadResponse">
import {computed, ref, useTemplateRef, type PropType} from 'vue'
import {useTranslation} from 'i18next-vue'
import axios from 'axios'
import Rails from '@rails/ujs'
import {isNonEmptyString} from '@/js/js-utils'

const {t: $t} = useTranslation()

const props = defineProps({
  disabledMessage: {default: null, type: String as PropType<string | null>},
  inputId: {default: null, type: String},
  inputName: {default: null, type: String},
  title: {default: null, type: String},
  uploadData: {default: null, type: Object},
  uploadUrl: {default: null, type: String},
})

const emit = defineEmits<{
  'upload-error': [error: string]
  'upload-progress': [isUploading: boolean, uploadProgress?: number, message?: string]
  'uploaded': [data: TUploadResponse]
}>()

const uploadField = useTemplateRef<HTMLInputElement>('uploadField')

const isUploading = ref(false)
const uploadProgress = ref(0)

const hasUploadUrl = computed((): boolean => {
  return isNonEmptyString(props.uploadUrl)
})

const disabled = computed((): boolean => {
  return isUploading.value || !hasUploadUrl.value
})

const uploadFile = (): void => {
  if (!hasUploadUrl.value) {
    alert('No upload URL defined!')
    return
  }

  if (disabled.value) {
    return
  }

  if (!uploadField.value?.files) {
    return
  }

  const file = uploadField.value.files[0]
  if (file === null) {
    return
  }

  const formData = new FormData()
  formData.append('file', file)
  if (props.uploadData) {
    for (const prop in props.uploadData) {
      formData.append(prop, props.uploadData[prop])
    }
  }

  uploadField.value.value = ''

  isUploading.value = true
  uploadProgress.value = 0

  emit('upload-progress', isUploading.value, uploadProgress.value, 'Uploading file ...')

  axios.post(props.uploadUrl!, formData, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': Rails.csrfToken()
    },
    onUploadProgress: (progressEvent) => {
      uploadProgress.value = Math.floor(progressEvent.loaded / progressEvent.total! * 100)
      if (progressEvent.loaded === progressEvent.total) {
        emit('upload-progress', isUploading.value, uploadProgress.value, 'Processing file ...')
      } else {
        emit('upload-progress', isUploading.value, uploadProgress.value, 'Uploading file ...')
      }
    }
  })
    .then((response) => {
      let error = null as string | null
      if (response.data) {
        if (response.data.error) {
          error = response.data.error
        } else {
          emit('uploaded', response.data)
        }
      } else {
        error = 'Invalid response from server'
      }

      isUploading.value = false
      emit('upload-progress', isUploading.value)
      if (error) {
        emit('upload-error', error)
      }
    })
    .catch((error) => {
      isUploading.value = false
      emit('upload-error', error.message)
    })
}

const onUploadClick = (e: Event): void => {
  if (disabled.value) {
    e.preventDefault()
    return
  }

  if (isNonEmptyString(props.disabledMessage)) {
    alert(props.disabledMessage)
    e.preventDefault()
  }
}
</script>
